// Room-name sheets, from the website's side.
//
// UMapsTools authors one CSV per building (`buildingfiles/<slug>/<slug>.csv`,
// columns Room Number / Room Name / Room Type, written by Python's csv.writer:
// CRLF, minimal quoting) and `sync-to-umapsweb.py` copies it to
// `public/Map Info/pathfinder/RoomNames/<slug>.csv`. The pathfinder reads
// those through `parseRoomNamesCsv` in `src/pathfinder/lib/dataSource.js`,
// which keeps only rows WITH a name — right for a map, wrong for an editor
// that has to show the 5,000 rooms still waiting for one. So this module
// parses the whole sheet, writes it back in the same bytes the generator
// would, and holds the few rules the Room Names tool and its export share.
//
// ⚠️ `serializeRoomSheet` must keep producing exactly what
// `scripts/generate-room-sheets.py` produces: the exported CSVs are drop-in
// replacements for the authored files, and a formatting difference turns a
// two-name change into a 300-line diff somebody has to read past.
//
// Pure — no browser API, no import — so `node src/utils/roomSheets.test.mjs`
// runs it against the real Armes sheet.

export const SHEET_HEADER = ['Room Number', 'Room Name', 'Room Type'];

/** One CSV line → cells. Double-quoted fields, with `""` for a literal quote. */
export function parseCsvLine(line) {
  const out = []; let cur = ''; let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) {
      if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; }
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === ',') { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out;
}

/** The whole sheet, blank names included, in file order. */
export function parseRoomSheet(text) {
  const rows = [];
  const lines = String(text || '').replace(/^﻿/, '').split(/\r?\n/);
  for (let i = 1; i < lines.length; i++) { // row 0 is the header
    if (!lines[i].trim()) continue;
    const [roomNumber = '', roomName = '', roomType = ''] = parseCsvLine(lines[i]);
    const rn = roomNumber.trim();
    if (!rn) continue;
    rows.push({ roomNumber: rn, roomName: roomName.trim(), roomType: roomType.trim() });
  }
  return rows;
}

/** Python csv.writer's QUOTE_MINIMAL: quote only what needs it. */
function cell(v) {
  const s = v == null ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Rows → the bytes the generator writes (CRLF, trailing newline, no BOM). */
export function serializeRoomSheet(rows) {
  const lines = [SHEET_HEADER.map(cell).join(',')];
  for (const r of rows) lines.push([r.roomNumber, r.roomName || '', r.roomType || ''].map(cell).join(','));
  return lines.join('\r\n') + '\r\n';
}

/** Stairwells and elevators ("105st", "128el") — the pathfinder's own rule. */
export function isShaftRoom(roomNumber) {
  return /\d{2}[a-z]?(st|el)$/i.test(String(roomNumber || '').trim());
}

/** The generator's `natural_key`: digit runs compare as numbers. */
export function naturalCompare(a, b) {
  const ka = String(a).split(/(\d+)/), kb = String(b).split(/(\d+)/);
  for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
    const x = ka[i] ?? '', y = kb[i] ?? '';
    if (x === y) continue;
    const nx = /^\d+$/.test(x), ny = /^\d+$/.test(y);
    if (nx && ny) { const d = Number(x) - Number(y); if (d) return d; continue; }
    if (nx !== ny) return nx ? -1 : 1; // the generator sorts ints before strings
    const d = x.toLowerCase() < y.toLowerCase() ? -1 : x.toLowerCase() > y.toLowerCase() ? 1 : 0;
    if (d) return d;
  }
  return 0;
}

const ROOM_POLY_TYPES = new Set(['room', 'stair', 'elevator']);

/**
 * The rows a sheet WOULD have, for a building that has none yet — the
 * generator's `room_numbers()`: polygon room numbers (rooms, stairs,
 * elevators) plus nav `room_doors`, first spelling wins, naturally sorted.
 */
export function roomsFromFiles(struct, nav) {
  const seen = new Map(); // lower → first spelling
  for (const p of struct?.polygons || []) {
    const rn = String(p?.room_number || '').trim();
    if (rn && ROOM_POLY_TYPES.has(p.type) && !seen.has(rn.toLowerCase())) seen.set(rn.toLowerCase(), rn);
  }
  for (const d of nav?.room_doors || []) {
    const rn = String(d?.room_number || '').trim();
    if (rn && !seen.has(rn.toLowerCase())) seen.set(rn.toLowerCase(), rn);
  }
  return [...seen.values()].sort(naturalCompare).map((roomNumber) => ({ roomNumber, roomName: '', roomType: '' }));
}

/** The draft store's key — mirrors `roomKey` in api/_roomNames.js. */
export const roomKey = (slug, roomNumber) => `${slug}::${String(roomNumber).toLowerCase()}`;

/** The sheet as it would read with this building's drafts laid over it. */
export function applyDrafts(slug, rows, drafts) {
  return rows.map((r) => {
    const d = drafts?.[roomKey(slug, r.roomNumber)];
    return d ? { ...r, roomName: d.name } : r;
  });
}

/**
 * What the export has to say: every draft, with the authored name it replaces
 * so a reviewer can see the change rather than only the result. A draft for a
 * room the sheet does not list is kept (the apply script adds the row) and
 * flagged `unknownRoom`.
 */
export function buildChanges(buildings, drafts) {
  const bySlug = new Map(buildings.map((b) => [b.slug, b]));
  const out = [];
  for (const [id, d] of Object.entries(drafts || {})) {
    const b = bySlug.get(d.building);
    const row = b?.rows.find((r) => roomKey(d.building, r.roomNumber) === id) || null;
    out.push({
      building: d.building, buildingName: b?.name || d.building, roomNumber: row?.roomNumber || d.roomNumber,
      name: d.name, previous: row ? row.roomName : '', unknownRoom: !b || !row,
      by: d.by || '', updatedAt: d.updatedAt || '',
    });
  }
  return out.sort((a, b) => a.building.localeCompare(b.building) || naturalCompare(a.roomNumber, b.roomNumber));
}

/** A draft whose name the live sheet already carries is published, not pending. */
export function isPublished(change) {
  return !change.unknownRoom && change.name === change.previous;
}
